import React, { useEffect, useState } from 'react';
import { useDispatch } from "react-redux";
import { showSnackBar } from "../../components/Snackbar/Snackbar";
import { useLocation, useNavigate } from "react-router-dom";
import { useForgotPasswordFlow } from "../../features/auth/useForgotPasswordFlow";
import { SuccessCheckIcon } from "../../components/Icons/Icons";
import { NEUTRAL_RESET_CSS } from "../../components/NeutralAuth/neutralCardCss";
import NeutralPortalHeader from "../../components/NeutralAuth/NeutralPortalHeader";

const MIN_PASSWORD_LENGTH = 5

const ResetPassword = ({ neutral = false }) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordError, setNewPasswordError] = useState(false);
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [confirmNewPasswordError, setConfirmNewPasswordError] = useState(false);
  const [reqError, setReqError] = useState('');
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const reqEmail = searchParams.get('email');
  // navigate state, which the router persists into history and restores on reload. Absent only
  // when the code step was skipped — a pasted link, a new tab, a bookmark, a post-deploy reload.
  const code = location.state?.code;
  const flow = useForgotPasswordFlow()
  const isClient = !flow.isInternal
  const accent = flow.accent
  const accentHover = flow.accentHover
  const navigate = useNavigate()

  useEffect(() => {
    const handleReqError = () => {
      if (reqError && reqError.length > 0) {
        showSnackBar("error", reqError);
        setReqError('');
      }
    };

    handleReqError();
  }, [reqError]);

  useEffect(() => {
    setConfirmNewPasswordError(false)
    setNewPasswordError(false)
  }, [newPassword, confirmNewPassword]);

  const handleForgotPassword = async () => {
    if (newPassword !== confirmNewPassword) {
      setConfirmNewPasswordError(true);
      setNewPasswordError(true);
      return;
    }

    // Checked here as well as on the server so the most ordinary mistake on this page never
    // reaches the endpoint that would otherwise spend the confirmation code on it.
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setNewPasswordError(true);
      setReqError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters long`);
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const credentials = { email: reqEmail, newPassword, code };
      const response = await dispatch(flow.forgotPasswordThunk(credentials));

      if (response?.payload?.status === "success") {
        navigate(flow.signInPath, { replace: true })
        showSnackBar("success", "Password changed successfully")
      } else {
        const message = typeof response.payload === 'string'
          ? response.payload
          : response?.error?.errors?.[0]?.message
        setReqError(message)
      }
    } catch (error) {
      console.error('An error occurred while processing the forgot password request:', error);
      setReqError('An error occurred while processing your request.');
    }

    setIsSubmitting(false);
  };

  return (
    <>
      <style>{neutral ? NEUTRAL_RESET_CSS : `
        .rp-root {
          min-height: 100vh;
          background: #0f0f0f;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'DM Sans', sans-serif;
        }
        .rp-card {
          background: #161616;
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 16px;
          padding: 48px;
          width: 100%;
          max-width: 420px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
        }
        .rp-heading {
          color: #ffffff;
          font-size: 24px;
          font-weight: 600;
          margin: 0;
          text-align: center;
        }
        .rp-subtitle {
          color: rgba(255,255,255,0.45);
          font-size: 13px;
          line-height: 1.6;
          text-align: center;
          margin: 0;
        }
        .rp-form {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .rp-field {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .rp-label {
          color: rgba(255,255,255,0.45);
          font-size: 11px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          font-family: 'DM Sans', sans-serif;
        }
        .rp-input {
          width: 100%;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px;
          color: #ffffff;
          font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          padding: 12px 16px;
          outline: none;
          box-sizing: border-box;
          transition: border-color 0.2s;
        }
        .rp-input::placeholder {
          color: rgba(255,255,255,0.3);
        }
        .rp-input:focus {
          border-color: ${accent}99;
        }
        .rp-input--error {
          border-color: rgba(239,68,68,0.5);
        }
        .rp-error {
          color: #ef4444;
          font-size: 12px;
          margin: 0;
        }
        .rp-btn {
          width: 100%;
          background: ${accent};
          color: #ffffff;
          border: none;
          border-radius: 10px;
          padding: 14px;
          font-size: 14px;
          font-weight: 500;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          transition: background 0.2s;
        }
        .rp-btn:hover {
          background: ${accentHover};
        }
        .rp-link {
          color: rgba(255,255,255,0.45);
          font-size: 13px;
          text-decoration: none;
          transition: color 0.2s;
        }
        .rp-link:hover {
          color: rgba(255,255,255,0.7);
        }
      `}</style>
      <div className="rp-root">
        {neutral && <NeutralPortalHeader />}
        <div className="rp-card">
          <SuccessCheckIcon stroke={neutral ? 'currentColor' : `${accent}99`} />
          {!code ? (
            <>
              <h2 className="rp-heading">Your reset session expired</h2>
              <p className="rp-subtitle">
                For your security this step only works right after you enter the code we emailed you.
                Request a new code to continue.
              </p>
              <a className="rp-btn" href={flow.forgotPasswordPath} style={{ textAlign: 'center', textDecoration: 'none', display: 'block' }}>
                Request a new code
              </a>
            </>
          ) : (
            <>
              <h2 className="rp-heading">Verified successfully!</h2>
              <p className="rp-subtitle">Create a new secure password.</p>
              <div className="rp-form">
                <div className="rp-field">
                  <label className="rp-label">New Password</label>
                  <input
                    className={`rp-input${newPasswordError ? ' rp-input--error' : ''}`}
                    type="password"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </div>
                <div className="rp-field">
                  <label className="rp-label">Confirm Password</label>
                  <input
                    className={`rp-input${confirmNewPasswordError ? ' rp-input--error' : ''}`}
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                  />
                  {confirmNewPasswordError && (
                    <p className="rp-error">passwords do not match</p>
                  )}
                </div>
                <button className="rp-btn" onClick={handleForgotPassword} disabled={isSubmitting}>
                  {isSubmitting ? 'Updating…' : 'Update Password'}
                </button>
              </div>
              <a className="rp-link" href={flow.forgotPasswordPath}>Request a new code</a>
            </>
          )}
          <a className="rp-link" href={flow.signInPath}>Return to sign in</a>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
